/**
 * worldline-palimpsest (Phase 3-1, Task 4.1) 筆跡 (trace) 分類純関数。
 *
 * palimpsest 2D view では筆跡を 3 段階に分類して視覚差を付ける:
 *  - `completed`: ending 到達済 (isEnded=true)
 *  - `ongoing`:  プレイヤーが現在プレイ中の筆跡 (= 現行 worldline)
 *  - `interrupted`: 非現行 かつ ending 未到達 (放置された分岐)
 *
 * `MIN_SCENES_TO_BE_FOOTPRINT` は UI 側で「足跡として描画するかの閾値」に使う定数
 * (このモジュールでは純関数の出力には影響しない)。`sceneCount < MIN_SCENES_TO_BE_FOOTPRINT`
 * な筆跡は足跡化が未確定な「ドラフト」として PalimpsestGrid が非表示にする等の判断に利用。
 *
 * design.md §Component Design §traceClassifier / Req 6.1-6.5 準拠。
 */

/** 足跡として描画するかを判断する scene 数の閾値 */
export const MIN_SCENES_TO_BE_FOOTPRINT = 3;

export type TraceStatus = 'completed' | 'ongoing' | 'interrupted';

/** 分類に必要な最小限の worldline の形 (Tanstack Query の WorldlineNode 互換) */
export type TraceWorldline = {
  id: string;
  isEnded: boolean;
};

/**
 * 筆跡を 3 段階に分類する。
 *
 * 判定優先順位:
 *  1. `isEnded` が true なら `completed` (現行フラグより優先)
 *  2. `worldline.id === currentWorldlineId` なら `ongoing`
 *  3. それ以外は `interrupted`
 *
 * @param worldline - 分類対象の worldline (id + isEnded)
 * @param sceneCount - 筆跡の scene 数。分類結果自体には影響しないが、API 整合上で受けておく
 *   (UI 側で MIN_SCENES_TO_BE_FOOTPRINT を参照するための将来拡張点)
 * @param currentWorldlineId - プレイヤーが現在プレイ中の worldline id。null は「現行なし」
 *   (例: まだプレイを開始していない状態)。
 */
export function classifyTrace(
  worldline: TraceWorldline,
  sceneCount: number,
  currentWorldlineId: string | null,
): TraceStatus {
  // sceneCount は将来の拡張用途。現時点では意図的に参照しない (lint 警告抑制)。
  void sceneCount;

  if (worldline.isEnded) return 'completed';
  if (currentWorldlineId !== null && worldline.id === currentWorldlineId) {
    return 'ongoing';
  }
  return 'interrupted';
}
